<script setup lang="ts">
import { UserPlus } from 'lucide-vue-next'
import { Link } from '@inertiajs/vue3'

interface Props {
  tutor: {
    id: number | string
    name: string
    slug?: string
    avatar?: string
    bio?: string
    specialization?: string
    videoCount?: number
    studentCount?: number
  }
  tutorLinkPrefix?: string
}

const props = withDefaults(defineProps<Props>(), {
  tutorLinkPrefix: '/tutors',
})

/**
 * Null when there is no photograph. The template draws initials instead —
 * building a ui-avatars.com URL sent the tutor's name to a third party.
 */
const getAvatarUrl = (_name: string) => props.tutor.avatar || null

const initialsOf = (name: string) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (parts[0]?.[0] || '?').toUpperCase()
}

const tutorHref = () => {
  if (!props.tutor.slug) return null
  return `${props.tutorLinkPrefix}/${props.tutor.slug}`
}
</script>

<template>
  <section id="tutor-info" class="bg-white rounded-xl border border-gray-200 p-6">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-4">
        <component
          :is="tutorHref() ? Link : 'div'"
          :href="tutorHref() || undefined"
        >
          <img
            v-if="getAvatarUrl(tutor.name)"
            :src="getAvatarUrl(tutor.name)"
            :alt="tutor.name"
            class="w-16 h-16 rounded-full border-2 border-gray-200 object-cover"
          />
          <span
            v-else
            class="grid w-16 h-16 place-items-center rounded-full bg-brand-soft text-lg font-semibold text-brand-soft-text"
            :aria-label="tutor.name"
            role="img"
          >{{ initialsOf(tutor.name) }}</span>
        </component>
        <div>
          <component
            :is="tutorHref() ? Link : 'h3'"
            :href="tutorHref() || undefined"
            class="font-bold text-gray-900 text-lg hover:text-blue-600 transition-colors"
          >
            {{ tutor.name }}
          </component>
          <p class="text-gray-600 text-sm">{{ tutor.specialization || 'Expert Tutor' }}</p>
          <p v-if="tutor.videoCount" class="text-gray-500 text-xs mt-1">
            <span v-if="tutor.videoCount">{{ tutor.videoCount }} videos</span>
            <!-- <span v-if="tutor.videoCount && tutor.studentCount"> &bull; </span>
            <span v-if="tutor.studentCount">{{ tutor.studentCount }} students</span> -->
          </p>
        </div>
      </div>
      <Link
        v-if="tutorHref()"
        :href="tutorHref()!"
        class="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
      >
        View Profile
      </Link>
      <button
        v-else
        class="px-6 py-2.5 bg-brand-blue text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors flex items-center gap-2"
      >
        <UserPlus class="w-4 h-4" />
        Follow
      </button>
    </div>
  </section>
</template>
